import React, { useEffect, useMemo, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Box,
    Checkbox
} from "@mui/material";
import { lightBlue } from "@mui/material/colors";
import { truncateGroup } from "../../../shared utils/personDetail";
import NavigateBefore from "@mui/icons-material/NavigateBefore";
import NavigateNext from "@mui/icons-material/NavigateNext";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AddTransferDialog from "../dialogs/AddTransferDialog.jsx";
import EditTransferDialog from "../dialogs/EditTransferDialog.jsx";
import ConfirmContactDialog from "../dialogs/ConfirmContactDialog.jsx";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import useTransportGroupsByDate from "../../hooks/useTransportGroupsByDate.js";
import useTransferMutations from "../../hooks/useTransferMutations.js";
import { filterTransfers, sortTransfers, groupTransfersByCampus } from "../../utils/transfers.js";
import { downloadTransfersXlsx } from "../../utils/transfersXlsx.js";
import { downloadTransfersJourneysPdf } from "../../utils/transfersJourneysPdf.js";
import TransfersToolbar from "./TransfersToolbar.jsx";
import TransfersSidebar from "./TransfersSidebar.jsx";
import { useAirports } from "../../hooks/useAirports.js";
import { makeEligibilityContext, busEligibilityForRow } from "../../utils/transportEligibility.js";
import { splitEligiblePassengers, summariseIneligible } from "../../utils/transportEligibility.js";
import { can } from "../../../shared utils/auth";

const TRANSFER_JOURNEY_TYPES = ["ARRIVALS", "DEPARTURES"];

// Sentinel for "on a bus that cannot handle this row" — never submitted, the
// option carrying it is disabled.
const OFF_ROUTE_VALUE = "__OFF_ROUTE__";

const journeyTypeOf = (g) =>
    String(g?.journeyType ?? g?.meta?.journeyType ?? "").trim().toUpperCase();

// Which kind of bus can actually handle this row. An arrival is handled by an
// arrivals bus and nothing else; INTERNAL sits on either. Without this, any
// coach the person happens to be on that day counts as their airport pickup.
const journeyTypesForDirection = (direction) => {
    const d = String(direction || "").toUpperCase();
    if (d === "IN") return ["ARRIVALS"];
    if (d === "OUT") return ["DEPARTURES"];
    return TRANSFER_JOURNEY_TYPES;
};

export default function TransfersTable({
    transfers = [],
    fetchTransfers,
    selectedDate,
    setSelectedDate,
    campuses,
    onTransferChanged,
    missingFlights = [],
    internalTransfers = [],
}) {
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState({});
    const [airportFilter, setAirportFilter] = useState("");
    const [nameFilter, setNameFilter] = useState("");
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastSeverity, setToastSeverity] = useState("success");
    const [groupChanges, setGroupChanges] = useState([]);
    const [addTransportDialogOpen, setAddTransportDialogOpen] = useState(false);
    const [selectedPersonKeys, setSelectedPersonKeys] = useState([]);
    const [bulkGroupId, setBulkGroupId] = useState("");
    const { loading: mutationLoading, addTransfers, editTransfers, deleteTransferById } = useTransferMutations();
    const { airports } = useAirports({ activeOnly: true });
    const {
        transportGroups,
        availableStatuses,
        loading: transportLoading,
        saveGroupChanges,
        createAndScheduleGroup,
        updateGroupStatus,
        updateTransportGroup,
        unassignPassengersFromGroup,
        confirmTransportGroup,
        unconfirmTransportGroup,
        saveTransportGroupContact,
    } = useTransportGroupsByDate(selectedDate);

    // Group awaiting contact details before it can be confirmed.
    const [confirmContactGroup, setConfirmContactGroup] = useState(null);
    
    const targetFromKey = (key) => {
        const [kind, idStr] = String(key).split(":");
        const id = Number(idStr);
        return kind === "A"
        ? { personType: "ADULT", adultId: id }
        : { personType: "STUDENT", studentId: id };
    };

    const [addDialog, setAddDialog] = useState({
        open: false,
        studentId: null,
        adultId: null,
        personType: "STUDENT",
        direction: "IN",
        date: null,
        });

    const [editDialog, setEditDialog] = useState({
        open: false,
        studentId: null,
        adultId: null,
        personType: "STUDENT",
        transferId: null,
        date: null,
    });

    const isAdultRow = (t) =>
        String(t?.personType || "").toUpperCase() === "ADULT" ||
        (t?.adultId && Number(t.adultId) !== 0);

    const personKeyFor = (t) => (isAdultRow(t) ? `A:${t.adultId}` : `S:${t.studentId}`);

    const displayId = (t) => (isAdultRow(t) ? `A${t.adultId}` : t.studentId);

    const toTarget = (t) =>
        isAdultRow(t)
        ? { personType: "ADULT", adultId: t.adultId }
        : { personType: "STUDENT", studentId: t.studentId };

    const openAddDialog = ({ personType, studentId, adultId }, direction, date) => {
        setAddDialog({ open: true, personType, studentId: studentId ?? null, adultId: adultId ?? null, direction, date });
    };


    const closeAddDialog = () => {
        setAddDialog((p) => ({ ...p, open: false }));
    };

    const openEditDialog = ({ personType, studentId, adultId }, transferId, date) => {
        setEditDialog({ open: true, personType, studentId: studentId ?? null, adultId: adultId ?? null, transferId, date });
    };

    const closeEditDialog = () => {
        setEditDialog((p) => ({ ...p, open: false }));
    };

    const busy = loading || transportLoading || mutationLoading;

    // Putting someone on a bus is a transport change, whichever screen it is
    // made from — the server applies the same rule to the membership
    // endpoints this table uses. Reading the allocation stays USER.
    const canEditTransport = can("transport");

    const filtered = useMemo(
        () => filterTransfers(transfers, { airportFilter, nameFilter }),
        [transfers, airportFilter, nameFilter]
    );

    const sortedTransfers = useMemo(
        () => sortTransfers(filtered, selectedDate),
        [filtered, selectedDate]
    );

    const groupedByCampus = useMemo(
        () => groupTransfersByCampus(sortedTransfers),
        [sortedTransfers]
    );

    const eligibilityCtx = useMemo(
        () => makeEligibilityContext({ airports, campuses }),
        [airports, campuses]
    );

    // Only arrival/departure groups belong on the transfers page — the feed
    // also returns ACTIVITY buses, which must not be assignable here.
    const transferGroups = useMemo(
        () => (transportGroups || []).filter((g) => TRANSFER_JOURNEY_TYPES.includes(journeyTypeOf(g))),
        [transportGroups]
    );

    useEffect(() => {
        setGroupChanges([]);
    }, [selectedDate]);

    const showToast = (msg, severity = "success") => {
        setToastMessage(msg);
        setToastSeverity(severity);
        setToastOpen(true);
    };

    const toggleCampus = (campus) => {
        setExpanded((prev) => ({
            ...prev,
            [campus]: !prev[campus],
        }));
    };

    const handleFetch = (date) => {
        setLoading(true);
        fetchTransfers(date).finally(() => setLoading(false));
    };

    const handlePrevDay = () => {
        const newDate = selectedDate.subtract(1, "day");
        setSelectedDate(newDate);
        handleFetch(newDate);
    };

    const handleNextDay = () => {
        const newDate = selectedDate.add(1, "day");
        setSelectedDate(newDate);
        handleFetch(newDate);
    };

    const handleTransportChange = (target, previousGroupId, newGroupId) => {
        const personType = String(target?.personType || "").toUpperCase();
        const isAdult = personType === "ADULT" || (target?.adultId && Number(target.adultId) !== 0);

        const personKey = isAdult ? `A:${target.adultId}` : `S:${target.studentId}`;

        setGroupChanges((prev) => {
            const updated = prev.filter((c) => c.personKey !== personKey);
            return [
                ...updated,
                {
                    personKey,
                    personType: isAdult ? "ADULT" : "STUDENT",
                    studentId: isAdult ? null : target.studentId,
                    adultId: isAdult ? target.adultId : null,
                    previousGroup: previousGroupId,
                    newGroup: newGroupId || null,
                },
            ];
        });
    };


    const handleSaveChanges = async () => {
        if (groupChanges.length === 0) {
            showToast("No changes to save.", "info");
            return;
        }

        setLoading(true);
        try {
            const result = await saveGroupChanges(groupChanges);
            showToast(
                result.ok ? "Transport allocations updated." : "Some updates failed.",
                result.ok ? "success" : "warning"
            );
            setGroupChanges([]);
            setSelectedPersonKeys([]);
            fetchTransfers(selectedDate);
        } catch (e) {
            console.error(e);
            showToast("Error saving changes.", "error");
        } finally {
            setLoading(false);
        } 
    };

    const getSelectedTransfersWithIds = () => {
        const selectedSet = new Set(selectedPersonKeys);
        return transfers
            .filter((t) => selectedSet.has(personKeyFor(t)) && t.transferId && t.transferId !== 0)
            .map((t) => ({ transferId: t.transferId }));
    };
    
    const editTransfer = async (transfer, originTarget, originTransferId) => {
        const isAdult = String(originTarget?.personType || "").toUpperCase() === "ADULT";
        const isMulti = selectedPersonKeys.length > 1;

        const targets =
        selectedPersonKeys.length > 1
        ? getSelectedTransfersWithIds()
        : [{ transferId: originTransferId }];


        if (selectedPersonKeys.length > 1 && targets.length !== selectedPersonKeys.length) {
            showToast("Cannot bulk edit: some selected people have no transfer to edit.", "error");

            return;
        }

        if (targets.length > 1) {
            const confirmed = window.confirm(`Apply changes to ${targets.length} transfers?`);
            if (!confirmed) return;
        }

        const result = await editTransfers({ transfer, targets });
        const label = isMulti ? "person" : (isAdult ? "adult" : "student");
        showToast(
            result.ok
            ? `Transfer updated for ${targets.length} ${label}${targets.length > 1 ? "s" : ""}`
            : "Some updates failed",
            result.ok ? "success" : "warning"
        );

        setSelectedPersonKeys([]);
        handleFetch(selectedDate);
        onTransferChanged?.();
    };

    const addTransfer = async (transfer, originTarget) => {
        const isAdult = String(originTarget?.personType || "").toUpperCase() === "ADULT";
        const isMulti = selectedPersonKeys.length > 1;
        const targets =
            selectedPersonKeys.length > 1
            ? selectedPersonKeys.map(targetFromKey)
            : [originTarget];

        if (targets.length > 1) {
            const confirmed = window.confirm(`Apply this transfer to ${targets.length} people?`);
            if (!confirmed) return;
        }

        const result = await addTransfers({ transfer, targets });
        const label = isMulti ? "person" : (isAdult ? "adult" : "student");
        showToast(
            result.ok
            ? `Transfer added for ${targets.length} ${label}${targets.length > 1 ? "s" : ""}`
            : "Some transfers failed",
            result.ok ? "success" : "warning"
        );

        setSelectedPersonKeys([]);
        handleFetch(selectedDate);
        onTransferChanged?.();
    };

    const handleDownload = async () => {
        try {
            await downloadTransfersXlsx({ sortedTransfers, transportGroups, selectedDate });
        } catch (err) {
            console.error("Transfers XLSX export failed:", err);
            showToast(`Export failed: ${err?.message || err}`, "error");
        }
    };

    const handleDownloadJourneysPdf = () => {
        try {
            downloadTransfersJourneysPdf({
                sortedTransfers,
                transportGroups,
                selectedDate,
                missingFlights,
                internalTransfers,
            });
        } catch (err) {
            console.error("Journeys PDF export failed:", err);
            showToast(`PDF export failed: ${err?.message || err}`, "error");
        }
    };

    const handleAddTransportGroup = async (groupData) => {
        const result = await createAndScheduleGroup(groupData);
        if (!result.ok) {
            showToast("Failed to create transport group", "error");
            return;
        }
        showToast(
            result.scheduled ? "Group created and scheduled" : "Group created but not scheduled",
            result.scheduled ? "success" : "warning"
        );
        fetchTransfers(selectedDate);
    };

    const deleteTransfer = async (t) => {
        const transferId = t?.transferId;
        if (!transferId) return;

        const confirmed = window.confirm("Delete this transfer?");
        if (!confirmed) return;

        const result = await deleteTransferById(transferId);

        if (result.ok) {
            showToast("Transfer deleted", "success");
            const key = personKeyFor(t);
            setSelectedPersonKeys(prev => prev.filter(k => k !== key));
            handleFetch(selectedDate);
            onTransferChanged?.();
        } else {
            showToast(`Failed to delete transfer (${result.status})`, "error");
        }
    };

    const handleStatusChange = async (group, newStatus) => {
        const groupId = group.id;
        const isCurrentlyConfirmed =
            group.status === "Confirmed" || Boolean(group.confirmedAt);

        // Always confirm via the dialog (never silently): it lets the user
        // review/edit the recorded contact and acknowledges that they will be
        // recorded as the confirmer. Confirmation goes through the dedicated
        // /confirm endpoint, consistent with the Transport board.
        if (newStatus === "Confirmed") {
            setConfirmContactGroup(group);
            return;
        }

        // Moving away from Confirmed: clear the stamp first so we don't leave a
        // group that reads Confirmed without a valid confirmation.
        if (isCurrentlyConfirmed) {
            const un = await unconfirmTransportGroup(groupId);
            if (!un.ok) {
                showToast("Failed to update status", "error");
                return;
            }
            if (newStatus !== "Planned") {
                const res = await updateGroupStatus(groupId, newStatus);
                if (!res.ok) {
                    showToast("Failed to update status", "error");
                    return;
                }
            }
            showToast("Group status updated");
            return;
        }

        const res = await updateGroupStatus(groupId, newStatus);
        if (res.ok) showToast("Group status updated");
        else showToast("Failed to update status", "error");
    };

    // Save the supplied contact onto the group, then confirm it.
    const handleConfirmWithContact = async ({ contactName, contactNumber }) => {
        const group = confirmContactGroup;
        if (!group) return;

        const saveRes = await saveTransportGroupContact(group.id, contactName, contactNumber);
        if (!saveRes.ok) {
            showToast("Failed to save contact details", "error");
            return;
        }

        const res = await confirmTransportGroup(group.id);
        setConfirmContactGroup(null);
        if (res.ok) showToast("Group confirmed");
        else showToast("Failed to confirm group", "error");
    };

    const handleEditTransportGroup = async (groupId, patch) => {
        const existing = transportGroups.find((g) => String(g.id) === String(groupId));

        // If unconfirming, clear the stamp first so the basics/route update below
        // applies to a Planned group.
        if (patch.unconfirm) {
            const un = await unconfirmTransportGroup(groupId);
            if (!un.ok) {
                showToast("Failed to unconfirm group", "error");
                return un;
            }
        }

        const res = await updateTransportGroup(groupId, patch, existing);
        if (!res.ok) {
            showToast("Failed to update transport group", "error");
            return res;
        }

        // Persist contact (empty values are preserved server-side via COALESCE).
        if (String(patch.contactName || "").trim() || String(patch.contactNumber || "").trim()) {
            await saveTransportGroupContact(groupId, patch.contactName, patch.contactNumber);
        }

        // Confirm last — it requires the contact to already be stored.
        if (patch.confirm) {
            const c = await confirmTransportGroup(groupId);
            if (!c.ok) {
                showToast(
                    c.contactRequired
                        ? "Contact name and number are required to confirm"
                        : "Failed to confirm group",
                    "error"
                );
                return c;
            }
        }

        showToast("Transport group updated", "success");
        return { ok: true };
    };

    return (
        <>
            <TransfersToolbar
                selectedDate={selectedDate}
                onPrevDay={handlePrevDay}
                onNextDay={handleNextDay}
                onDateChange={(newDate) => {
                    setSelectedDate(newDate);
                    fetchTransfers(newDate);
                }}
                airportFilter={airportFilter}
                onAirportFilterChange={setAirportFilter}
                nameFilter={nameFilter}
                onNameFilterChange={setNameFilter}
            />

            {busy ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    <TableContainer component={Paper} sx={{ margin: "0 auto", maxWidth: 1900 }}>
                        <Table
                            sx={{
                                minWidth: 800,
                                color: lightBlue[200],
                                margin: "0 auto",
                            }}
                            size="small"
                            aria-label="simple table"
                        >
                            <TableHead>
                                <TableRow sx={{ bgcolor: lightBlue[100] }}>
                                    <TableCell sx={{ border: 1.5 }} align="center">ID</TableCell>
                                    <TableCell sx={{ border: 1.5 }} align="center">Dir.</TableCell>
                                    <TableCell sx={{ border: 1.5 }} align="center">Name</TableCell>
                                    <TableCell sx={{ border: 1.5 }} align="center">Flight</TableCell>
                                    <TableCell sx={{ border: 1.5 }} align="center">Departure</TableCell>
                                    <TableCell sx={{ border: 1.5 }} align="center">Arrival</TableCell>
                                    <TableCell sx={{ border: 1.5 }} align="center">Arrival/Departure Time</TableCell>
                                    <TableCell sx={{ border: 1.5 }} align="center">UM</TableCell>
                                    <TableCell sx={{ border: 1.5 }} align="center">Notes</TableCell>
                                    <TableCell sx={{ border: 1.5 }} align="center">Transfer Status</TableCell>
                                    <TableCell sx={{ border: 1.5 }} align="center">Edit</TableCell>
                                    <TableCell sx={{ border: 1.5 }} align="center">Multi</TableCell>
                                    <TableCell sx={{ border: 1.5 }} align="center">Transport</TableCell>
                                    <TableCell sx={{ border: 1.5 }} align="center">Transport Status</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {Object.entries(groupedByCampus).map(([campus, campusTransfers]) => (
                                    <React.Fragment key={campus}>
                                        <TableRow>
                                            <TableCell
                                                colSpan={15}
                                                sx={{ bgcolor: lightBlue[50], fontWeight: "bold" }}
                                            >
                                                <IconButton onClick={() => toggleCampus(campus)} size="small">
                                                    {expanded[campus] ? <NavigateBefore /> : <NavigateNext />}
                                                </IconButton>
                                                {campus}
                                            </TableCell>
                                        </TableRow>

                                        {expanded[campus] &&
                                            campusTransfers.map((t, index) => {
                                                
                                                const isAdult = isAdultRow(t);

                                                const personKey = isAdult ? `A:${t.adultId}` : `S:${t.studentId}`;

                                                const isMember = (g) =>
                                                    isAdult ? g.adultIds?.includes(t.adultId) : g.studentIds?.includes(t.studentId);

                                                // The bus that actually handles this transfer: a transfer bus, going
                                                // the right way. Scoping this to every transport group for the date
                                                // is what made an evening excursion coach colour an arrival green
                                                // while its airport pickup had never been allocated.
                                                const allowedTypes = journeyTypesForDirection(t.direction);
                                                const liveGroup = transferGroups.find(
                                                    (g) => isMember(g) && allowedTypes.includes(journeyTypeOf(g))
                                                );

                                                // They may still be on a bus that cannot handle this row — an
                                                // activity coach, or a departures bus against an arrival. Rendering
                                                // nothing would show "None", which reads identically to genuinely
                                                // unassigned, so the select names it as a disabled option instead.
                                                const offRouteGroup = liveGroup
                                                    ? null
                                                    : (transportGroups || []).find(isMember);

                                                const change = groupChanges.find((c) => c.personKey === personKey);

                                                const hasGroup = !!liveGroup;
                                                // Self-transfer is recorded as flightId "SELFTRANSFER" (the
                                                // "private pickup" tick in Add/Edit Transfer), not a boolean.
                                                const isSelfTransfer =
                                                    String(t.flightId || "").trim().toUpperCase() === "SELFTRANSFER";
                                                const status = liveGroup?.status;

                                                let backgroundColor;
                                                if (status === "Complete") backgroundColor = "#e0e0e0";
                                                // Self-transfer needs no transport, so it reads as done — the same
                                                // green as a confirmed transport.
                                                else if (status === "Confirmed" || isSelfTransfer) backgroundColor = "#b3ffb3";
                                                else if (hasGroup) backgroundColor = "#e6ffe6";

                                                return (
                                                    <TableRow
                                                        key={`${campus}-${t.direction}-${isAdult ? `A${t.adultId}` : `S${t.studentId}`}-${index}`}
                                                        sx={{ bgcolor: backgroundColor }}
                                                    >
                                                        <TableCell align="center">{displayId(t)}</TableCell>
                                                        <TableCell align="center">{t.direction}</TableCell>
                                                        <TableCell align="center">
                                                            {t.name}
                                                            {t.bookingGroupName ? (
                                                                <span style={{ opacity: 0.7, fontSize: "0.85em" }}>
                                                                    {" "}— {truncateGroup(t.bookingGroupName)}
                                                                </span>
                                                            ) : null}
                                                            {t.staff ? (
                                                                <span style={{ opacity: 0.7, fontSize: "0.85em" }}>
                                                                    {" "}— staff
                                                                </span>
                                                            ) : null}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {t.direction === "INTERNAL" ? "" : t.flightId}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {t.direction === "INTERNAL" ? t.fromCampus : t.departing}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {t.direction === "INTERNAL" ? t.toCampus : t.destination}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {t.direction === "INTERNAL"
                                                            ? t.departureTime || "—"
                                                            : t.direction === "IN"
                                                            ? t.arrivalTime || "—"
                                                            : t.departureTime || "—"}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {t.direction === "INTERNAL" ? "—" : t.um ? "Yes" : "No"}
                                                        </TableCell>
                                                        <TableCell align="center">{t.notes || "—"}</TableCell>

                                                        <TableCell align="center">
                                                            {t.direction === "INTERNAL" || (t.transferId && t.transferId !== 0)
                                                                ? <CheckCircleIcon sx={{ color: "green" }} titleAccess="Planned" />
                                                                : <ErrorOutlineIcon sx={{ color: "red" }} titleAccess="Missing Data" />
                                                            }
                                                        </TableCell>

                                                        <TableCell align="center">
                                                            <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                                                                {!t.transferId || t.transferId === 0 ? (
                                                                    <IconButton
                                                                        color="primary"
                                                                        size="small"
                                                                        onClick={() => openAddDialog(toTarget(t), t.direction, selectedDate.toDate())}
                                                                    >
                                                                        <AddIcon />
                                                                    </IconButton>
                                                                ) : (
                                                                    <>
                                                                        <IconButton
                                                                            color="primary"
                                                                            size="small"
                                                                            onClick={() => openEditDialog(toTarget(t), t.transferId, selectedDate.toDate())}
                                                                        >
                                                                            <EditIcon />
                                                                        </IconButton>

                                                                        <IconButton
                                                                            color="error"
                                                                            size="small"
                                                                            onClick={() => deleteTransfer(t)}
                                                                        >
                                                                            <DeleteIcon />
                                                                        </IconButton>
                                                                    </>
                                                                )}
                                                            </Box>
                                                        </TableCell>

                                                        <TableCell padding="checkbox">
                                                            <Checkbox
                                                                checked={selectedPersonKeys.includes(personKey)}
                                                                onChange={(e) => {
                                                                    const checked = e.target.checked;
                                                                    const key = personKey;
                                                                    setSelectedPersonKeys((prev) =>
                                                                        checked ? [...prev, key] : prev.filter((k) => k !== key)
                                                                    );
                                                                }}
                                                            />
                                                        </TableCell>

                                                        <TableCell align="center">
                                                            <select
                                                                disabled={!canEditTransport}
                                                                title={canEditTransport ? "" : "Changing transport needs the Transport grant"}
                                                                style={{
                                                                    width: 260,          // pick a number that fits your table
                                                                    maxWidth: "100%",
                                                                    boxSizing: "border-box",
                                                                    padding: "6px 8px",
                                                                    borderRadius: 6,
                                                                    border: "1px solid rgba(0,0,0,0.23)",
                                                                    background: "white",
                                                                    font: "inherit",
                                                                }}
                                                                value={
                                                                    change
                                                                        ? (change.newGroup === null ? "" : String(change.newGroup))
                                                                        : liveGroup
                                                                            ? String(liveGroup.id)
                                                                            : offRouteGroup
                                                                                ? OFF_ROUTE_VALUE
                                                                                : ""
                                                                }
                                                                onChange={(e) => {
                                                                    const newGroupId = e.target.value === "" ? null : Number(e.target.value);
                                                                    handleTransportChange(
                                                                        isAdult ? { personType: "ADULT", adultId: t.adultId } : { personType: "STUDENT", studentId: t.studentId },
                                                                        liveGroup?.id ?? null,
                                                                        newGroupId
                                                                    );
                                                                }}
                                                            >
                                                                <option value="">None</option>
                                                                {offRouteGroup && (
                                                                    <option value={OFF_ROUTE_VALUE} disabled>
                                                                        {`On ${offRouteGroup.groupName} — not a ${
                                                                            String(t.direction).toUpperCase() === "OUT" ? "departures" : "arrivals"
                                                                        } bus`}
                                                                    </option>
                                                                )}
                                                                    {transferGroups.map((g) => {
                                                                        const currentMembers = (g.studentIds?.length || 0) + (g.adultIds?.length || 0);
                                                                        const added = groupChanges.filter((c) => String(c.newGroup) === String(g.id)).length;
                                                                        const removed = groupChanges.filter(
                                                                            (c) => String(c.previousGroup) === String(g.id) && String(c.newGroup) !== String(g.id)
                                                                        ).length;
                                                                        const projectedTotal = currentMembers + added - removed;
                                                                        const isFull = projectedTotal >= g.capacity;

                                                                        // ✅ eligibility rules (per passenger row t, per bus g)
                                                                        const eligibility = busEligibilityForRow(t, g, eligibilityCtx);

                                                                        const disabled = isFull || !eligibility.ok;
                                                                        const label = `${g.groupName} (${projectedTotal}/${g.capacity})` +
                                                                            (eligibility.ok ? "" : ` — ${eligibility.reason}`);

                                                                        return (
                                                                            <option key={g.id} value={String(g.id)} disabled={disabled}>
                                                                                {label}
                                                                            </option>
                                                                        );
                                                                    })}
                                                            </select>
                                                        </TableCell>

                                                        <TableCell align="center">
                                                            {(() => {
                                                                // Deliberately the row-scoped liveGroup: this select calls
                                                                // handleStatusChange, so the unscoped version let someone set
                                                                // an excursion coach to Complete from a transfers row.
                                                                if (!liveGroup) return "—";

                                                                return (
                                                                    <select
                                                                        value={liveGroup.status || ""}
                                                                            onChange={(e) => handleStatusChange(liveGroup, e.target.value)}
                                                                    >
                                                                        {availableStatuses.map((s) => (
                                                                            <option key={s.id} value={s.status}>
                                                                                {s.status}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                );
                                                            })()}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TransfersSidebar
                        campuses={campuses}
                        airports={airports}
                        transfers={transfers}
                        eligibilityCtx={eligibilityCtx}
                        unassignPassengersFromGroup={unassignPassengersFromGroup}
                        onSaveTransportChanges={handleSaveChanges}
                        onDownload={handleDownload}
                        onDownloadJourneysPdf={handleDownloadJourneysPdf}
                        onOpenAddGroup={() => setAddTransportDialogOpen(true)}
                        addTransportDialogOpen={addTransportDialogOpen}
                        setAddTransportDialogOpen={setAddTransportDialogOpen}
                        onAddTransportGroup={handleAddTransportGroup}
                        onEditTransportGroup={handleEditTransportGroup}
                        bulkGroupId={bulkGroupId}
                        setBulkGroupId={setBulkGroupId}
                        transportGroups={transportGroups}
                        selectedPersonKeys={selectedPersonKeys}
                        onBulkApplyToSelected={() => {
                            const selectedGroupId = bulkGroupId === "" ? null : Number(bulkGroupId);

                            // removing from bus ("None") should always be allowed
                            if (selectedGroupId === null) {
                                selectedPersonKeys.forEach((key) => {
                                    const target = targetFromKey(key);
                                    const isAdult = target.personType === "ADULT";
                                    const personId = isAdult ? target.adultId : target.studentId;

                                    const liveGroupId =
                                        transportGroups.find((g) =>
                                            isAdult ? g.adultIds?.includes(personId) : g.studentIds?.includes(personId)
                                        )?.id ?? null;

                                    handleTransportChange(target, liveGroupId, null);
                                });

                                // ✅ clear selection after applying
                                setSelectedPersonKeys([]);
                                setBulkGroupId("");
                                return;
                            }

                            const bus = transportGroups.find((g) => String(g.id) === String(selectedGroupId));
                            if (!bus) {
                                // ✅ still clear selection if user tried to apply
                                setSelectedPersonKeys([]);
                                setBulkGroupId("");
                                return;
                            }

                            const selectedRows = transfers.filter((t) => selectedPersonKeys.includes(personKeyFor(t)));

                            const { eligible, ineligible } = splitEligiblePassengers(selectedRows, bus, eligibilityCtx);

                            eligible.forEach((t) => {
                                const target = toTarget(t);
                                const isAdult = target.personType === "ADULT";
                                const personId = isAdult ? target.adultId : target.studentId;

                                const liveGroupId =
                                    transportGroups.find((g) =>
                                        isAdult ? g.adultIds?.includes(personId) : g.studentIds?.includes(personId)
                                    )?.id ?? null;

                                handleTransportChange(target, liveGroupId, selectedGroupId);
                            });

                            if (ineligible.length > 0) {
                                showToast(
                                    `Skipped ${ineligible.length} not eligible: ${summariseIneligible(ineligible)}`,
                                    "warning"
                                );
                            }

                            // ✅ clear selection after applying
                            setSelectedPersonKeys([]);
                            setBulkGroupId("");
                        }}
                    />                  
                </Box>
            )}

            <AddTransferDialog
                open={addDialog.open}
                onClose={closeAddDialog}
                transferDate={addDialog.date}
                direction={addDialog.direction}
                studentId={addDialog.studentId}
                adultId={addDialog.adultId}
                personType={addDialog.personType}
                campuses={campuses}
                onSaved={(payload, target) => addTransfer(payload, target)}
            />

            <EditTransferDialog
                open={editDialog.open}
                onClose={closeEditDialog}
                transferDate={editDialog.date}
                studentId={editDialog.studentId}
                adultId={editDialog.adultId}
                personType={editDialog.personType}
                transferId={editDialog.transferId}
                campuses={campuses}
                onSaved={(payload, target, transferId) => editTransfer(payload, target, transferId)}
            />

            <ConfirmContactDialog
                open={Boolean(confirmContactGroup)}
                group={confirmContactGroup}
                onClose={() => setConfirmContactGroup(null)}
                onConfirm={handleConfirmWithContact}
            />

            <Snackbar
                open={toastOpen}
                autoHideDuration={4000}
                onClose={() => setToastOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <MuiAlert
                    onClose={() => setToastOpen(false)}
                    severity={toastSeverity}
                    elevation={6}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {toastMessage}
                </MuiAlert>
            </Snackbar>
        </>
    );
}
